/**
 * Admin settings contracts: developer display names, per-model token
 * pricing, and global cost settings. Persisted by the API
 * (api/src/settingsStore.ts) and edited from the web app's Settings view.
 *
 * Everything here is display/costing configuration. It never changes the
 * underlying telemetry, and the only setting that reaches a KQL query is the
 * developer exclusion list — whose ids are validated by developerIdSchema
 * exactly like the `developerId` filter (see api/src/queries/adoption.ts
 * developerFilter).
 */
import { z } from 'zod';
import { developerIdSchema, toolSchema } from './api.js';
const money = z.number().finite().nonnegative().max(100_000);
const optionalText = (max) => z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v ? v : undefined));
export const currencyCodeSchema = z.string().regex(/^[A-Z]{3}$/, 'Currency must be a three-letter ISO 4217 code');
/**
 * One developer's admin-maintained profile. Rows exist only for developers an
 * admin has configured; a developer with no row shows their raw id.
 */
export const developerProfileSchema = z.object({
    developerId: developerIdSchema,
    /** Shown in place of the username/hash everywhere in the dashboard. */
    displayName: optionalText(100),
    team: optionalText(100),
    /** Test/service accounts: dropped from every dashboard query (not from the telemetry itself). */
    excluded: z.boolean().default(false),
});
/**
 * Model ids as the collectors report them. Narrow charset: never reaches KQL
 * (pricing is applied in the API after the query), but it is rendered and
 * used as a lookup key, so keep it to what real model ids look like.
 */
export const modelIdSchema = z
    .string()
    .trim()
    .min(1)
    .max(100)
    .regex(/^[\w.:@/+-]+$/, 'Model id may only contain letters, digits, and . : @ / + - _');
export const modelProviderSchema = z.enum(['anthropic', 'openai', 'other']);
/** Token prices for one model, in the configured currency per million tokens. */
export const tokenPricesSchema = z.object({
    inputPerMillion: money,
    outputPerMillion: money,
    /** Cache-read (Claude Code) / cached-input (Codex) tokens. Omitted = priced at 0. */
    cacheReadPerMillion: money.optional(),
    /** Cache-creation tokens (Claude Code only). Omitted = priced at 0. */
    cacheWritePerMillion: money.optional(),
});
export const isoDaySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a YYYY-MM-DD date');
export const MODEL_TIERS = ['fast', 'mid', 'frontier'];
export const modelTierSchema = z.enum(MODEL_TIERS);
/** Output list price (per million tokens) bounds used when a pricing row sets no tier. */
export const TIER_FAST_BELOW_OUTPUT = 6;
export const TIER_FRONTIER_FROM_OUTPUT = 20;
export function tierFromPrices(prices) {
    if (prices.outputPerMillion < TIER_FAST_BELOW_OUTPUT)
        return 'fast';
    return prices.outputPerMillion >= TIER_FRONTIER_FROM_OUTPUT ? 'frontier' : 'mid';
}
export const modelPricingSchema = tokenPricesSchema.extend({
    /** Exact model id, or its undated base id — see resolveModelPricing for how dated ids match. */
    model: modelIdSchema,
    provider: modelProviderSchema,
    notes: optionalText(200),
    /**
     * First UTC day these prices apply. A model may have several rows with
     * different dates (a price history); usage is priced at the row in force on
     * its day. Omitted = in force from the start.
     */
    effectiveFrom: isoDaySchema.optional(),
    /** Fast, mid, or frontier. Omitted = from the output price (see tierFromPrices). */
    tier: modelTierSchema.optional(),
    /** Context window in tokens. Omitted = from the price feed when it lists one. */
    contextWindow: z.number().int().min(1000).max(100_000_000).optional(),
});
export const costSettingsSchema = z.object({
    /** One currency for every price in settings — rates are entered in this currency. */
    currency: currencyCodeSchema,
    /** GitHub Copilot is priced per premium request (AIU), not per token. */
    copilotCostPerAiu: money,
    /** Applied to any Claude Code / Codex model with no pricing row. */
    fallbackPricing: tokenPricesSchema,
    /** Free-text basis shown next to every indicative cost (e.g. "Contract rate, FY26"). */
    source: z.string().trim().min(1).max(300),
    /**
     * Apply list prices from the public price feed to Model pricing on each
     * refresh (USD only). Off by default: settings usually hold contracted rates.
     */
    autoApplyPriceFeed: z.boolean().default(false),
});
/** When the built-in catalog (api/src/defaultSettings.ts) was last checked against published list prices. */
export const DEFAULT_CATALOG_CHECKED_AT = '2026-09-26';
/** Prices not reviewed for this long are flagged as possibly out of date. */
export const PRICES_STALE_AFTER_DAYS = 90;
/** When model prices were last saved or applied, else when the built-in catalog was checked. */
export function pricesLastReviewed(settings) {
    return settings.audit.models?.updatedAt ?? `${DEFAULT_CATALOG_CHECKED_AT}T00:00:00.000Z`;
}
export function pricesAreStale(settings, now = new Date()) {
    return now.getTime() - Date.parse(pricesLastReviewed(settings)) > PRICES_STALE_AFTER_DAYS * 24 * 60 * 60 * 1000;
}
/**
 * Executive view settings (Settings → Executive). Only shapes how the
 * executive summary is calculated from already-queried data; none of it
 * reaches KQL.
 */
export const executiveSettingsSchema = z.object({
    /**
     * Engineers expected to use AI tools — the adoption-rate denominator.
     * Omitted = use the number of developers seen in telemetry in the last 90 days.
     */
    engineersInScope: z.number().int().positive().max(1_000_000).optional(),
    /** Adoption goal shown as the target line/marker. */
    adoptionTargetPercent: z.number().int().min(1).max(100),
    /** A developer is "habitual" when active on at least this share of the period's working days. */
    habitualThresholdPercent: z.number().int().min(1).max(100),
    /** Teams with fewer configured members than this are merged into "Other" so no individual stands out. */
    minTeamSize: z.number().int().min(1).max(100),
});
export const DEFAULT_EXECUTIVE_SETTINGS = {
    adoptionTargetPercent: 80,
    habitualThresholdPercent: 60,
    minTeamSize: 5,
};
/**
 * Feature flags (Settings → Features): which tools and optimizers appear in
 * the sidebar, for every user. Navigation only — a hidden source's telemetry
 * is still collected and still counts in the aggregate views.
 */
export const FEATURE_FLAG_KEYS = ['copilot', 'claude-code', 'codex', 'kiro', 'gemini', 'rtk', 'headroom', 'graphify'];
export const featureFlagsSchema = z.object({
    copilot: z.boolean().default(true),
    'claude-code': z.boolean().default(true),
    codex: z.boolean().default(true),
    kiro: z.boolean().default(true),
    gemini: z.boolean().default(true),
    rtk: z.boolean().default(true),
    headroom: z.boolean().default(true),
    graphify: z.boolean().default(true),
});
export const DEFAULT_FEATURE_FLAGS = {
    copilot: true,
    'claude-code': true,
    codex: true,
    kiro: true,
    gemini: true,
    rtk: true,
    headroom: true,
    graphify: true,
};
/**
 * Data health settings (Settings → Data health). Only classifies developers on
 * the Data Health page; it never reaches a query.
 */
export const dataHealthSettingsSchema = z.object({
    /** A developer whose collector has not run (or sent anything) for this many hours is flagged as stale. */
    staleAfterHours: z.number().int().min(1).max(720),
});
export const DEFAULT_DATA_HEALTH_SETTINGS = { staleAfterHours: 24 };
const percentGoal = z.number().min(0).max(100).optional();
const amountGoal = z.number().positive().max(1_000_000_000).optional();
/**
 * Budgets and goals (Settings → Budgets & goals). Amounts are in the Cost tab's
 * currency and cover Claude Code and Codex indicative cost. Display only: no
 * alerts are sent, and none of it reaches a query.
 */
export const targetsSettingsSchema = z.object({
    /** Organization budget per calendar month (UTC). */
    monthlyBudget: amountGoal,
    teamBudgets: z
        .array(z.object({ team: z.string().trim().min(1).max(100), monthlyBudget: z.number().positive().max(1_000_000_000) }))
        .max(500)
        .default([])
        .refine((rows) => new Set(rows.map((r) => r.team.toLowerCase())).size === rows.length, 'Each team may only have one budget'),
    /** Cache hit ratio goals, in each tool's own terms (higher is better). */
    cacheHitGoalClaudeCode: percentGoal,
    cacheHitGoalCodex: percentGoal,
    /** Output tokens per unit of currency (higher is better). */
    outputPerCostGoalClaudeCode: amountGoal,
    outputPerCostGoalCodex: amountGoal,
    /** Most of the cost that should go to frontier-tier models (lower is better). */
    frontierShareMaxPercent: percentGoal,
    /** Most a month's cost per active developer should be (lower is better). */
    costPerDeveloperMax: amountGoal,
});
export const DEFAULT_TARGETS_SETTINGS = { teamBudgets: [] };
export const settingsAuditEntrySchema = z.object({
    updatedAt: z.string(), // ISO 8601
    updatedBy: z.string(),
});
export const settingsSectionSchema = z.enum(['developers', 'teams', 'models', 'cost', 'executive', 'features', 'dataHealth', 'targets']);
/**
 * Change history (Settings → History): one record per save that changed
 * something, each listing what changed with its value before and after.
 * Append-only — records are never edited or removed by the API. `account`
 * covers the login (a password change is recorded, never the password).
 */
export const settingsHistorySectionSchema = z.enum([...settingsSectionSchema.options, 'account']);
const settingsChangeValueSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);
export const settingsChangeSchema = z.object({
    /** What the change is about: a developer id, team, or model id. Omitted for section-wide values. */
    subject: z.string().optional(),
    /** The value that changed, e.g. `excluded`, `team`, `inputPerMillion`. */
    field: z.string(),
    /** null = was not set / did not exist. */
    before: settingsChangeValueSchema,
    /** null = cleared / removed. */
    after: settingsChangeValueSchema,
});
export const settingsHistoryRecordSchema = z.object({
    id: z.string(),
    at: z.string(), // ISO 8601
    by: z.string(),
    section: settingsHistorySectionSchema,
    changes: z.array(settingsChangeSchema),
    /**
     * Set on a follow-up record written when the save that record announced
     * did not complete — history is append-only, so the first record is
     * corrected by a second one rather than removed.
     */
    notSavedRecordId: z.string().optional(),
});
export const settingsHistoryQuerySchema = z.object({
    section: settingsHistorySectionSchema.optional(),
    limit: z.coerce.number().int().min(1).max(500).default(100),
});
export const settingsHistoryResponseSchema = z.object({
    /** Newest first. */
    records: z.array(settingsHistoryRecordSchema),
    /** Records matching the filter, before `limit` was applied. */
    total: z.number(),
});
function uniqueBy(items, key) {
    return new Set(items.map(key)).size === items.length;
}
export const developerProfilesSchema = z
    .array(developerProfileSchema)
    .max(2000)
    .refine((rows) => uniqueBy(rows, (r) => r.developerId.toLowerCase()), 'Each developer may only appear once');
export const teamsSchema = z
    .array(z.string().trim().min(1).max(100))
    .max(500)
    .refine((names) => uniqueBy(names, (name) => name.toLowerCase()), 'Each team may only appear once');
export const modelPricingListSchema = z
    .array(modelPricingSchema)
    .max(2000)
    .refine((rows) => uniqueBy(rows, (r) => `${r.model.toLowerCase()}@${r.effectiveFrom ?? ''}`), 'Each model id may only appear once per effective date');
export const appSettingsSchema = z.object({
    version: z.literal(1),
    developers: developerProfilesSchema,
    teams: teamsSchema.default([]),
    models: modelPricingListSchema,
    cost: costSettingsSchema,
    /** Added after version 1 shipped — files saved before it get the defaults. */
    executive: executiveSettingsSchema.default(DEFAULT_EXECUTIVE_SETTINGS),
    features: featureFlagsSchema.default(DEFAULT_FEATURE_FLAGS),
    dataHealth: dataHealthSettingsSchema.default(DEFAULT_DATA_HEALTH_SETTINGS),
    targets: targetsSettingsSchema.default(DEFAULT_TARGETS_SETTINGS),
    audit: z.object({
        developers: settingsAuditEntrySchema.optional(),
        teams: settingsAuditEntrySchema.optional(),
        models: settingsAuditEntrySchema.optional(),
        cost: settingsAuditEntrySchema.optional(),
        executive: settingsAuditEntrySchema.optional(),
        features: settingsAuditEntrySchema.optional(),
        dataHealth: settingsAuditEntrySchema.optional(),
        targets: settingsAuditEntrySchema.optional(),
    }),
});
export const assignedTeamsSchema = appSettingsSchema.superRefine((settings, ctx) => {
    const names = new Set(settings.teams.map((team) => team.toLowerCase()));
    settings.developers.forEach((developer, index) => {
        if (developer.team && !names.has(developer.team.toLowerCase())) {
            ctx.addIssue({ code: 'custom', path: ['developers', index, 'team'], message: `Add team "${developer.team}" before assigning it, or clear the assignment` });
        }
    });
});
export const updateDevelopersRequestSchema = z.object({ developers: developerProfilesSchema });
export const updateTeamsRequestSchema = z.object({ teams: teamsSchema });
export const updateModelsRequestSchema = z.object({ models: modelPricingListSchema });
export const updateCostRequestSchema = z.object({ cost: costSettingsSchema });
export const updateExecutiveRequestSchema = z.object({ executive: executiveSettingsSchema });
export const updateFeaturesRequestSchema = z.object({ features: featureFlagsSchema });
export const updateDataHealthRequestSchema = z.object({ dataHealth: dataHealthSettingsSchema });
export const updateTargetsRequestSchema = z.object({ targets: targetsSettingsSchema });
/**
 * Identifiers seen in telemetry recently, so an admin can map developers and
 * price models without typing ids by hand. Lists ids only — no usage totals
 * are merged across tools here (see dashboard/AGENTS.md "API rules").
 */
export const discoveredEntitiesSchema = z.object({
    days: z.number(),
    developers: z.array(z.object({
        developerId: developerIdSchema,
        tools: z.array(toolSchema),
        lastActive: z.string(),
    })),
    models: z.array(z.object({
        model: z.string(),
        tool: toolSchema,
        events: z.number(),
    })),
});
/** One price period from the public price feed: list prices in USD per million tokens. */
export const priceFeedPeriodSchema = tokenPricesSchema.extend({
    /** null = in force from the start of the feed's history. */
    effectiveFrom: isoDaySchema.nullable(),
});
/** One model id from Settings (or telemetry) compared with the feed. */
export const priceFeedModelSchema = z.object({
    model: z.string(),
    provider: modelProviderSchema,
    /** The feed's model id that matched, or null when the feed has no price for it. */
    feedModelId: z.string().nullable(),
    /** The feed's price history, oldest first; the last period is the current list price. */
    history: z.array(priceFeedPeriodSchema),
    /** The Settings row in force today, or null when the model is priced at the fallback rate. */
    applied: tokenPricesSchema.nullable(),
    /** The feed's history differs from the Settings rows for this model. */
    differs: z.boolean(),
});
export const priceFeedStatusSchema = z.object({
    enabled: z.boolean(),
    /** Where the feed is read from. */
    source: z.string(),
    /** Last successful download, or null if none yet. */
    fetchedAt: z.string().nullable(),
    /** Last attempt's error, or null when it succeeded. */
    error: z.string().nullable(),
    /** The feed is in USD; it can be applied only when Settings → Cost uses USD. */
    applicable: z.boolean(),
    autoApply: z.boolean(),
    models: z.array(priceFeedModelSchema),
});
export const applyPriceFeedRequestSchema = z.object({
    /** Model ids to update from the feed; omitted = every model that differs. */
    models: z.array(modelIdSchema).max(500).optional(),
});
/**
 * Dated snapshot suffixes that still match their base model's pricing row:
 * `claude-sonnet-4-5-20250929`, `gpt-4o-mini-2024-07-18`, `claude-opus-4-5@20251101`,
 * `codex-mini-latest`. Anything else (e.g. `gpt-5.6` vs `gpt-5`) is a
 * different model and must have its own row.
 */
const SNAPSHOT_SUFFIX = /^(?:[-@]\d{8}|-\d{4}-\d{2}-\d{2}|-latest)$/;
/**
 * Picks the pricing row for a reported model id: an exact (case-insensitive)
 * match first, then a row whose id is the model minus a dated snapshot
 * suffix, else the fallback rate. Among that model's dated rows, the one in
 * force on `onDay` (YYYY-MM-DD) wins; without a day, the latest one. Shared so
 * the API (which prices) and the Settings page (which flags unpriced models)
 * always agree.
 */
export function resolveModelPricing(model, models, fallback, onDay) {
    const id = model.trim().toLowerCase();
    let rows = models.filter((m) => m.model.toLowerCase() === id);
    if (rows.length === 0) {
        const base = models
            .filter((m) => {
            const candidate = m.model.toLowerCase();
            return id.startsWith(candidate) && SNAPSHOT_SUFFIX.test(id.slice(candidate.length));
        })
            .sort((a, b) => b.model.length - a.model.length)[0];
        if (base)
            rows = models.filter((m) => m.model.toLowerCase() === base.model.toLowerCase());
    }
    if (rows.length === 0)
        return { prices: fallback, pricedAs: null };
    // Undated rows sort first: they apply from the start.
    const byDate = [...rows].sort((a, b) => (a.effectiveFrom ?? '').localeCompare(b.effectiveFrom ?? ''));
    const inForce = onDay ? byDate.filter((r) => (r.effectiveFrom ?? '') <= onDay).at(-1) : byDate.at(-1);
    // A day before the earliest dated row still uses that row: the best price known.
    const row = inForce ?? byDate[0];
    return { prices: row, pricedAs: row.model };
}
/** A model's tier: its current pricing row's tier, else from the price it is charged at (fallback included). */
export function resolveModelTier(model, models, fallback) {
    const { prices } = resolveModelPricing(model, models, fallback);
    return prices.tier ?? tierFromPrices(prices);
}
/** The context window set on any of the model's pricing rows (the latest wins), or null. */
export function resolveContextWindow(model, models) {
    const { pricedAs } = resolveModelPricing(model, models, { inputPerMillion: 0, outputPerMillion: 0 });
    if (pricedAs === null)
        return null;
    const rows = models
        .filter((m) => m.model.toLowerCase() === pricedAs.toLowerCase() && m.contextWindow !== undefined)
        .sort((a, b) => (a.effectiveFrom ?? '').localeCompare(b.effectiveFrom ?? ''));
    return rows.at(-1)?.contextWindow ?? null;
}
export function priceTokenUsageByType(usage, prices) {
    return {
        input: (usage.inputTokens / 1_000_000) * prices.inputPerMillion,
        output: (usage.outputTokens / 1_000_000) * prices.outputPerMillion,
        cacheRead: (usage.cacheReadTokens / 1_000_000) * (prices.cacheReadPerMillion ?? 0),
        cacheWrite: (usage.cacheWriteTokens / 1_000_000) * (prices.cacheWritePerMillion ?? 0),
    };
}
export function priceTokenUsage(usage, prices) {
    const byType = priceTokenUsageByType(usage, prices);
    return byType.input + byType.output + byType.cacheRead + byType.cacheWrite;
}
//# sourceMappingURL=settings.js.map